import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AccordionButtonProps } from '@/shared/components/Accordion/Accordion.types'
import {
  isFullRefundBookingError,
  openBookingRefundSupportEmail
} from '@/shared/functions/bookingRefundSupportEmail/bookingRefundSupportEmail.functions'
import useResponsiveness from '@/shared/hooks/responsive.hooks'
import { Alert, Box, Grid, Typography } from '@mui/material'

import Button from '../../shared/components/Button/Button.component'
import Dropdown from '../../shared/components/Dropdown/Dropdown.component'
import Modal from '../../shared/components/Modal/Modal.component'
import Stepper from '../../shared/components/Stepper/Stepper.component'
import TripOverview from '../TripSummary/components/TripOverview/TripOverview.component'
import DeliveryDetails from './components/DeliveryDetails/DeliveryDetails.component'
import DropoffLocationStep from './components/DropoffLocationStep/DropoffLocationStep.component'
import PassengerDetails from './components/PassengerDetails/PassengerDetails.component'
import PaymentDetails from '@/shared/components/PaymentDetails/PaymentDetails.component'
import { PaymentSubmittedSuccessfully } from '@/shared/components/PaymentSubmittedSuccessfully/PaymentSubmittedSuccessfully.component'
import ResponsiveDisplayWrapper from '@/shared/components/ResponsiveDisplayWrapper/ResponsiveDisplayWrapper.component'

import env from '../../networkRequests/apiClient/env.config'
import {
  createFlightBooking,
  resetCreateFlightBooking
} from '../../redux/states/flights/createFlightBooking/createFlightBooking.slice'
import {
  CreateFlightBookingPassenger,
  CreateFlightBookingRequest
} from '../../redux/states/flights/createFlightBooking/createFlightBooking.types'
import {
  Country,
  FlightItinerary
} from '../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { resetGetBaggageDetails } from '../../redux/states/flights/getBaggageDetails/getBaggageDetails.slice'
import { getCountriesAndCities } from '../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import { CountryAndCities } from '../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.types'
import { FareOffer } from '../../redux/states/flights/getFlightFares/types/FareDetailsResponse.types'
import { resetGetSeatDetails } from '../../redux/states/flights/getSeatDetails/getSeatDetails.slice'
import { getUpdatedFlightOffer } from '../../redux/states/flights/getUpdatedFlightOffer/getUpdatedFlightOffer.slice'
import { GetUpdatedFlightOfferRequestPayload } from '../../redux/states/flights/getUpdatedFlightOffer/getUpdatedFlightOffer.types'
import { checkOnboardingInfo } from '../../redux/states/payments/checkOnboardingInfo/checkOnboardingInfo.slice'
import {
  CheckOnboardingInfoSuccessResponse,
  OnboardingInfoType
} from '../../redux/states/payments/checkOnboardingInfo/checkOnboardingInfo.types'
import { createAccountSession } from '../../redux/states/payments/createAccountSession/createAccountSession.slice'
import { CreateAccountSessionRequestPayload } from '../../redux/states/payments/createAccountSession/createAccountSession.types'
import {
  createFlightBookingPaymentIntent,
  resetCreateFlightBookingPaymentIntent
} from '../../redux/states/payments/createFlightBookingPaymentIntent/createFlightBookingPaymentIntent.slice'
import {
  CreateFlightBookingPaymentIntentRequestPayload,
  CreateFlightBookingPaymentIntentSuccessResponse,
  StripeSession
} from '../../redux/states/payments/createFlightBookingPaymentIntent/createFlightBookingPaymentIntent.types'
import {
  resetValidateUspsAddress,
  validateUspsAddress
} from '../../redux/states/usps/validateUspsAddress/validateUspsAddress.slice'
import { RootState } from '../../redux/store/store.types'
import router from '../../router/functions/router.functions'
import routes from '../../router/routes.dictionary'
import ModalFunctions from '../../shared/components/Modal/Modal.functions'
import Snackbar from '../../shared/components/Snackbar/Snackbar.functions'
import { Step } from '../../shared/components/Stepper/Stepper.types'
import { PaymentFormRef } from '../../shared/components/StripeCheckoutForm/StripeCheckoutForm.types'
import { buildUspsZipCodeForAddressRequest } from '../../shared/components/USPSLocationFinder/USPSLocationFinder.functions'
import date from '../../shared/functions/Date/date.functions'
import localStorage from '../../shared/functions/LocalStorage/localStorage'
import { getCountries } from '../../shared/functions/Places/places.functions'
import { sanitizePhoneNumber } from '../../shared/functions/String/string.functions'
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux.hooks'
import { ApiErrorResponse } from '../../shared/types/api.types'
import {
  ACCOUNT_SESSION_STORAGE_KEY,
  CREATE_FLIGHT_BOOKING_PAYMENT_INTENT_STORAGE_KEY,
  FARE_OFFER_STORAGE_KEY,
  FLIGHT_BOOKING_SUBMISSION_STORAGE_KEY,
  FLIGHT_ITINERARY_STORAGE_KEY
} from './FlightBooking.dictionary'
import styles from './FlightBooking.styles'
import { FlightBookingNavigationProps } from './FlightBooking.types'
import { DeliveryDetailsValues } from './components/DeliveryDetails/DeliveryDetails.types'
import { PassengerDetailsValues } from './components/PassengerDetails/PassengerDetails.types'
import { BaggageSelections } from './components/PassengerDetails/components/BaggageDetails/components/BaggageDetailsModal/BaggageDetailsModal.types'
import { SeatSelections } from './components/PassengerDetails/components/SeatDetails/components/SeatDetailsModal/SeatDetailsModal.types'

// Todo: handle when flight itinerary is too late to be booked
const FlightBooking: React.FC = () => {
  const paymentIntent = router.getUrlParam('payment_intent')
  const redirectStatusFromPayment = router.getUrlParam('redirect_status')
  // getUrlParam returns '' (not null) when the query key is missing. After a setup-intent
  // redirect the URL has redirect_status=succeeded but no payment_intent — treating '' as
  // present made paymentSuccess true and kept re-firing the redirect effect.
  const paymentSuccess = redirectStatusFromPayment === 'succeeded' && Boolean(paymentIntent)

  const [activeStep, setActiveStep] = useState(paymentSuccess ? 4 : 1) // Updated from 3 to 4 for booking confirmation
  const [passengerDetails, setPassengerDetails] = useState<PassengerDetailsValues[] | null>(null)
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetailsValues | null>(null)
  const [isPassengerDetailsFormValid, setIsPassengerDetailsFormValid] = useState(false)
  const [isDeliveryDetailsFormValid, setIsDeliveryDetailsFormValid] = useState(false)
  const [isPaymentFormValid, setIsPaymentFormValid] = useState(false)
  const [isOnboadingComlpete, setIsOnboadingComplete] = useState(false)
  const [paymentRequestLoading, setPaymentRequestLoading] = useState(false)
  const [setupPaymentLoading, setSetupPaymentLoading] = useState(false)
  const [stripeConnectAccountCountry, setStripeConnectAccountCountry] = useState('')
  const [showCountryDropdownModal, setShowCountryDropdownModal] = useState(false)
  const [forceReloadPaymentForm] = useState(false)
  const [accountSession, setAccountSession] = useState<StripeSession | null>(
    localStorage.getItem(ACCOUNT_SESSION_STORAGE_KEY)
  )
  const [disabledLocationIDs, setDisabledLocationIDs] = useState<Set<string>>(new Set())
  // const [orderSubmission, setOrderSubmission] = useState<CreateFlightBookingRequest | null>(null)

  const { loading: createFlightBookingPaymentIntentLoading, success: createFlightBookingPaymentIntentSuccess } =
    useAppSelector((state: RootState) => state.createFlightBookingPaymentIntent)

  const { loading: createAccountSessionLoading } = useAppSelector((state: RootState) => state.createAccountSession)

  const { loading: validateAddressLoading } = useAppSelector((state: RootState) => state.validateUspsAddress)

  const {
    loading: createFlightBookingLoading,
    success: createFlightBookingSuccess,
    error: createFlightBookingError
  } = useAppSelector((state: RootState) => state.createFlightBooking)

  const countriesAndCities = useAppSelector((state: RootState) => state.getCountriesAndCities.success)
  const userProfile = useAppSelector((state: RootState) => state.getProfile.success?.data)
  const { loading: getUpdatedFlightOfferLoading } = useAppSelector((state: RootState) => state.getUpdatedFlightOffer)

  // TODO: Consolidate all country data into a single source of truth
  const countries = getCountries()

  const dispatch = useAppDispatch()
  const [isDesktop] = useResponsiveness()
  const navigationProps = router.getNavigationProps() as FlightBookingNavigationProps
  const navigate = router.navigate()

  const paymentFormRef = useRef<PaymentFormRef>(null)
  const createFlightBookingSubmissionStartedRef = useRef(false)
  const stripeRedirectHandledRef = useRef(false)

  const [baggageSelections, setBaggageSelections] = useState<BaggageSelections>({})
  const [seatSelections, setSeatSelections] = useState<SeatSelections>({})

  const fareOffer = useMemo(() => {
    return (navigationProps?.state?.fareOffer as FareOffer) || localStorage.getItem(FARE_OFFER_STORAGE_KEY)
  }, [navigationProps])

  const flightItinerary =
    navigationProps?.state?.itinerary || (localStorage.getItem(FLIGHT_ITINERARY_STORAGE_KEY) as FlightItinerary)

  const fulfillmentOrderLegIndex = useMemo(() => {
    const legs = flightItinerary?.itinerary?.legs
    if (!legs?.length) return -1

    return legs.findIndex((leg) => leg.fulfillmentOrdersDetails?.length > 0)
  }, [flightItinerary?.itinerary?.legs])

  const fulfillmentOrderSlice = useMemo(() => {
    if (fulfillmentOrderLegIndex < 0 || !fareOffer?.slices?.length) return null

    return fareOffer.slices[fulfillmentOrderLegIndex] ?? null
  }, [fareOffer?.slices, fulfillmentOrderLegIndex])

  const arrivalAirportIataCode = useMemo(() => {
    if (fulfillmentOrderSlice) {
      return (fulfillmentOrderSlice.destination as { iata_code?: string })?.iata_code ?? null
    }

    if (!fareOffer?.slices?.length) return null

    return (fareOffer.slices[0].destination as { iata_code?: string })?.iata_code ?? null
  }, [fareOffer?.slices, fulfillmentOrderSlice])

  const useSplitDeliverySteps = useMemo(() => {
    const hasFulfillmentOrder = fulfillmentOrderLegIndex >= 0
    if (!hasFulfillmentOrder) return false

    const fulfillmentSliceDestination = fulfillmentOrderSlice?.destination as { iata_country_code?: string } | undefined
    const isUsDestination = fulfillmentSliceDestination?.iata_country_code === 'US'

    const hasNoItemRequest = (flightItinerary?.itinerary?.legs ?? []).every(
      (leg) =>
        !leg.offerRequestsDetails?.length ||
        leg.offerRequestsDetails.every((offerRequest) => !offerRequest.itemRequestDetails?.length)
    )

    return Boolean(isUsDestination && hasNoItemRequest)
  }, [fulfillmentOrderLegIndex, fulfillmentOrderSlice, flightItinerary?.itinerary?.legs])

  const itemsSourceCountry = navigationProps?.state?.sourceCountry as Country | undefined

  const effectiveSourceCountry = useMemo((): Country | undefined | null => {
    if (itemsSourceCountry?.iataCode) return itemsSourceCountry

    const relevantSlice = fulfillmentOrderSlice ?? fareOffer?.slices?.[0]
    const destination = relevantSlice?.destination as { iata_country_code?: string; name?: string } | undefined
    const iataCountryCode = destination?.iata_country_code
    if (!iataCountryCode) return undefined

    const countryNameFromList = (countriesAndCities?.data as CountryAndCities[] | undefined)?.find(
      (country) => country.iataCountryCode === iataCountryCode
    )?.countryName

    return {
      iataCode: iataCountryCode,
      name:
        countryNameFromList && countryNameFromList !== 'Unknown'
          ? countryNameFromList
          : iataCountryCode === 'US'
            ? 'United States'
            : iataCountryCode
    }
  }, [itemsSourceCountry, fareOffer?.slices, fulfillmentOrderSlice, countriesAndCities?.data])

  const setupIntent = router.getUrlParam('setup_intent')
  const redirectStatusFromSetup = router.getUrlParam('redirect_status')
  const setupPaymentDetailsSuccess = redirectStatusFromSetup === 'succeeded' && Boolean(setupIntent)

  const flightBookingPaymentIntent: CreateFlightBookingPaymentIntentSuccessResponse | null = useMemo(() => {
    return (
      createFlightBookingPaymentIntentSuccess || localStorage.getItem(CREATE_FLIGHT_BOOKING_PAYMENT_INTENT_STORAGE_KEY)
    )
  }, [createFlightBookingPaymentIntentSuccess])

  const { loading: checkOnboardingInfoLoading } = useAppSelector((state: RootState) => state.checkOnboardingInfo)

  useEffect(() => {
    return () => {
      dispatch(resetGetBaggageDetails())
      dispatch(resetGetSeatDetails())

      localStorage.removeItem(ACCOUNT_SESSION_STORAGE_KEY)
      localStorage.removeItem(FLIGHT_BOOKING_SUBMISSION_STORAGE_KEY)
      localStorage.removeItem(FARE_OFFER_STORAGE_KEY)
      localStorage.removeItem(CREATE_FLIGHT_BOOKING_PAYMENT_INTENT_STORAGE_KEY)
    }
  }, [dispatch])

  useEffect(() => {
    dispatch(getCountriesAndCities({}))
    window.scrollTo(0, 0)
  }, [dispatch])

  const isTransfer = useMemo(() => {
    const isReceivingPayment =
      flightBookingPaymentIntent?.data?.total && Number(flightBookingPaymentIntent?.data?.total) < 0

    return isReceivingPayment
  }, [flightBookingPaymentIntent])

  // A traveler who has already onboarded to Stripe Connect must not be held at the
  // Receive Payment step waiting to do it again. `onExit` from the embedded Connect
  // component is otherwise the only thing that sets this flag, and it never fires for an
  // account with nothing left to submit — so Submit Order stayed disabled forever.
  // Pressing it still re-verifies server side through checkOnboardingInfo, so this
  // loosens the button, not the check.
  useEffect(() => {
    if (isTransfer && flightBookingPaymentIntent?.data?.isCustomerOnboardedToStripeConnect) {
      setIsOnboadingComplete(true)
    }
  }, [isTransfer, flightBookingPaymentIntent])

  // Once the wizard has shown a Receive Payment step it must keep it, even if isTransfer
  // goes false underneath us — which it does on success, because handleSubmitOrder's
  // onSuccess resets the payment intent that isTransfer is derived from. Without this
  // latch confirmationStepIndex slides 5 -> 4 while activeStep sits at 5, every render
  // branch misses, and a booking that actually succeeded shows a blank page.
  const [hasShownPayoutStep, setHasShownPayoutStep] = useState(false)

  useEffect(() => {
    if (isTransfer) {
      setHasShownPayoutStep(true)
    }
  }, [isTransfer])

  const wizardHasPayoutStep = Boolean(isTransfer) || hasShownPayoutStep

  const paymentStepIndex = useSplitDeliverySteps ? 4 : 3
  const stayAddressStepIndex = 2
  const dropoffStepIndex = 3
  const receivePaymentStepIndex = useSplitDeliverySteps ? 5 : 4
  const confirmationStepIndex = wizardHasPayoutStep ? (useSplitDeliverySteps ? 6 : 5) : useSplitDeliverySteps ? 5 : 4

  const handleRebookNavigation = useCallback(() => {
    dispatch(resetCreateFlightBooking())
    navigate(routes.requests.path)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const handleRenewOfferNavigation = useCallback(() => {
    const payload: GetUpdatedFlightOfferRequestPayload = {
      request: {
        id: flightItinerary.id.toString()
      },

      onSuccess: (success) => {
        if (success.data.offer) {
          dispatch(resetCreateFlightBooking())
          localStorage.removeItem(FARE_OFFER_STORAGE_KEY)
          localStorage.removeItem(FLIGHT_ITINERARY_STORAGE_KEY)

          navigate(routes.tripSummary.path, {
            state: {
              fare: success.data.offer,
              isBookingFlight: true,
              isRenewedOffer: true,
              itinerary: flightItinerary
            }
          })
        }
      },

      onError: () => {
        navigate(routes.requests.path)
      }
    }

    dispatch(getUpdatedFlightOffer(payload))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate])

  const handleSubmitOrder = useCallback(() => {
    const order: CreateFlightBookingRequest | null = localStorage.getItem(FLIGHT_BOOKING_SUBMISSION_STORAGE_KEY)

    if (!order) {
      return
    }

    if (createFlightBookingSubmissionStartedRef.current) {
      return
    }
    createFlightBookingSubmissionStartedRef.current = true

    {
      const onSuccess = () => {
        dispatch(resetCreateFlightBookingPaymentIntent())

        localStorage.removeItem(FLIGHT_BOOKING_SUBMISSION_STORAGE_KEY)
        localStorage.removeItem(FARE_OFFER_STORAGE_KEY)
        localStorage.removeItem(CREATE_FLIGHT_BOOKING_PAYMENT_INTENT_STORAGE_KEY)

        if (isTransfer) {
          setActiveStep(confirmationStepIndex)
        }

        window.scrollTo(0, 0)
      }

      const onError = (error?: ApiErrorResponse) => {
        if (!error) {
          return
        }

        if (isFullRefundBookingError(error.userFriendlyMessage)) {
          const travelerFirstName = flightItinerary?.traveler?.firstName ?? ''
          const travelerLastName = flightItinerary?.traveler?.lastName ?? ''
          const travelerDisplayName = `${travelerFirstName} ${travelerLastName}`.trim()

          ModalFunctions.show({
            title: 'Error Booking Flight',
            icon: 'error',
            body: error.userFriendlyMessage,
            primaryButton: {
              label: 'Contact Support',
              onClick: () => {
                openBookingRefundSupportEmail({
                  apiError: error,
                  travelerDisplayName: travelerDisplayName || 'Traveler',
                  travelerUserId: flightItinerary?.traveler?.userId ?? ''
                })
              }
            },
            secondaryButton: {
              label: 'Dismiss'
            }
          })

          return
        }

        const issueCodes = error.details.map((detail) => detail.issue)

        for (const issueCode of issueCodes) {
          switch (issueCode) {
            case 'offer_no_longer_available':
            case 'invalid_offer_id':
            case 'airline_internal_error':
              handleRenewOfferNavigation()
              break

            default:
              ModalFunctions.show({
                title: 'Error Booking Flight',
                body: `${error.userFriendlyMessage} Let's try rebooking your flight.`,
                primaryButton: {
                  label: 'Rebook Flight',
                  onClick: handleRebookNavigation
                },
                secondaryButton: {
                  label: 'Dismiss'
                }
              })
              break
          }
        }
      }

      const onIdCheckError = () => {
        setAccountSession(null)
        localStorage.removeItem(ACCOUNT_SESSION_STORAGE_KEY)
        setIsOnboadingComplete(false)

        if (flightBookingPaymentIntent?.data?.setupIntent) {
          setSetupPaymentLoading(true)

          const mainPassengerPhoneNumber = passengerDetails?.[0]?.phoneNumber

          const payload: CreateAccountSessionRequestPayload = {
            request: {
              countryCode: stripeConnectAccountCountry,
              phoneNumber: sanitizePhoneNumber(mainPassengerPhoneNumber)
            },

            onSuccess: (response) => {
              if (flightBookingPaymentIntent) {
                const newAccountSession = {
                  secret: response.data.secret,
                  expiresAt: response.data.expiresAt
                }

                setAccountSession(newAccountSession)
                setSetupPaymentLoading(false)
              }
            },

            onError: () => {
              setSetupPaymentLoading(false)

              ModalFunctions.show({
                title: 'Error',
                body: 'Failed to create account session. Please try again.',
                primaryButton: {
                  label: 'OK'
                }
              })
            }
          }

          dispatch(createAccountSession(payload))
        }

        ModalFunctions.show({
          title: 'ID Verification Required',
          subtitle: 'To continue with your booking, please follow these steps:',
          body: (
            <>
              <Typography variant="body1" component="div">
                1. Go to <strong>Personal Details</strong> and click <strong>Edit</strong> (top right).
              </Typography>

              <Typography variant="body1" component="div">
                2. Click <strong>Upload Document</strong> to provide your ID verification.
              </Typography>

              <Typography variant="body1" component="div">
                You will only have to do this <u>once</u> to enable future payouts to your bank account.
              </Typography>
            </>
          ),
          primaryButton: {
            label: 'Okay',
            onClick: () => {
              // Dismiss the modal
            }
          }
        })
      }

      const onIdCheckSuccess = (response: CheckOnboardingInfoSuccessResponse) => {
        if (response.data.value) {
          dispatch(
            createFlightBooking({
              request: order,
              onSuccess,
              onError
            })
          )
        } else {
          onIdCheckError()
        }
      }

      if (isTransfer) {
        dispatch(
          checkOnboardingInfo({
            request: {
              infoType: OnboardingInfoType.ID_DOCUMENT_UPLOADED
            },

            onSuccess: onIdCheckSuccess
          })
        )
      } else {
        dispatch(
          createFlightBooking({
            request: order,
            onSuccess,
            onError
          })
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isTransfer, handleRebookNavigation, confirmationStepIndex, flightItinerary])

  useEffect(() => {
    if (createFlightBookingSuccess) {
      return
    }

    if (!(paymentSuccess || setupPaymentDetailsSuccess)) {
      return
    }

    if (stripeRedirectHandledRef.current) {
      return
    }

    stripeRedirectHandledRef.current = true

    // On the payout branch the card set-up that just succeeded is NOT the end of the
    // booking. The order is submitted from the Receive Payment step, which carries the
    // Stripe Connect account the money is paid into — and handleSubmitOrder is
    // deliberately not called here for a transfer. Jumping straight to the confirmation
    // step therefore skipped the only step that books anything: nothing was submitted,
    // and the confirmation step rendered blank because it had no booking to report.
    setActiveStep(isTransfer ? receivePaymentStepIndex : confirmationStepIndex)

    if (!isTransfer) {
      handleSubmitOrder()
    }

    window.scrollTo(0, 0)
  }, [
    createFlightBookingSuccess,
    paymentSuccess,
    setupPaymentDetailsSuccess,
    handleSubmitOrder,
    isTransfer,
    confirmationStepIndex,
    receivePaymentStepIndex
  ])

  // A successful booking is the wizard's terminal event, so drive the confirmation off
  // THAT rather than off whoever last called setActiveStep. The payout branch cannot do it
  // imperatively: handleSubmitOrder's onSuccess resets the payment intent, isTransfer and
  // every step index derived from it are recomputed on the very next render, and the step
  // it set moments earlier no longer means what it meant. Stated as an effect the two
  // settle together — activeStep follows confirmationStepIndex whenever it changes.
  useEffect(() => {
    if (createFlightBookingSuccess) {
      setActiveStep(confirmationStepIndex)
    }
  }, [createFlightBookingSuccess, confirmationStepIndex])

  // TODO: Handle paymentFailure

  const handleStepClick = (step: number) => {
    if (step !== 0) {
      setActiveStep(step)
    }

    if (step < paymentStepIndex) {
      dispatch(resetCreateFlightBookingPaymentIntent())
    }
  }

  const steps: Step[] = useMemo(() => {
    if (useSplitDeliverySteps) {
      return [
        { label: 'Choose Flights' },
        { label: 'Passenger and Seat Details' },
        { label: 'Stay Address' },
        { label: 'Dropoff Location' },
        { label: 'Payment' },
        ...(wizardHasPayoutStep ? [{ label: 'Receive Payment' }] : []),
        { label: 'Booking Confirmation' }
      ]
    }

    return [
      { label: 'Choose Flights' },
      { label: 'Passenger and Seat Details' },
      { label: 'Delivery Details' },
      { label: 'Payment' },
      ...(wizardHasPayoutStep ? [{ label: 'Receive Payment' }] : []),
      { label: 'Booking Confirmation' }
    ]
  }, [wizardHasPayoutStep, useSplitDeliverySteps])

  const handleFormChange = useCallback((passengers: PassengerDetailsValues[] | null, isValid: boolean) => {
    if (passengers) {
      const areValuesEmpty = passengers.every((passenger) => passenger.issuingCountry === '')

      setPassengerDetails(passengers)
      setIsPassengerDetailsFormValid(isValid && !areValuesEmpty)
    }
  }, [])

  const handleDeliveryDetailsChange = useCallback((values: DeliveryDetailsValues | null, isValid: boolean) => {
    if (values) {
      setDeliveryDetails(values)
      setIsDeliveryDetailsFormValid(isValid)
    }
  }, [])

  const onBaggageSelectionsChange = useCallback((selections: BaggageSelections) => {
    setBaggageSelections(selections)
  }, [])

  const handleContinueToPayment = () => {
    if (!useSplitDeliverySteps || activeStep !== dropoffStepIndex) {
      setActiveStep(paymentStepIndex)

      return
    }

    const selectedLocation = deliveryDetails?.selectedUSPSLocation
    if (!selectedLocation) return

    const { locationData, locationID } = selectedLocation

    dispatch(
      validateUspsAddress({
        request: {
          streetAddress: locationData.address,
          city: locationData.city,
          state: locationData.state,
          ZIPCode: buildUspsZipCodeForAddressRequest(locationData)
        },
        onSuccess: () => {
          dispatch(resetValidateUspsAddress())
          setActiveStep(paymentStepIndex)
        },
        onError: () => {
          dispatch(resetValidateUspsAddress())

          setDisabledLocationIDs((prev) => new Set(prev).add(locationID))

          if (deliveryDetails) {
            setDeliveryDetails({
              ...deliveryDetails,
              selectedUSPSLocation: null
            })
          }

          Snackbar.show({
            message: 'This USPS location address could not be validated. Please select another location.',
            severity: 'error'
          })
        }
      })
    )
  }

  const handleContinueToDeliveryDetails = () => {
    setActiveStep(2)
  }

  const onSeatSelectionsChange = useCallback((selections: SeatSelections) => {
    setSeatSelections(selections)
  }, [])

  const proceedWithPaymentSubmission = () => {
    localStorage.setItem(FARE_OFFER_STORAGE_KEY, fareOffer)
    localStorage.setItem(FLIGHT_ITINERARY_STORAGE_KEY, flightItinerary)

    if (passengerDetails && isPassengerDetailsFormValid && isPaymentFormValid && deliveryDetails) {
      const passengers: CreateFlightBookingPassenger[] = passengerDetails.map((passenger, index) => {
        const passengerBaggageDetails = Object.entries(baggageSelections).flatMap(([, passengerSelections]) => {
          const passengerBaggage = passengerSelections[passenger.id] || []

          return passengerBaggage.map((baggage) => ({
            baggageId: baggage.baggageId,
            segmentId: baggage.segmentId
          }))
        })

        const passengerSeatingDetails = Object.entries(seatSelections).flatMap(([segmentId, passengerSeats]) => {
          const seat = passengerSeats[passenger.id]

          return seat ? [{ seatId: seat.serviceId, segmentId }] : []
        })

        return {
          passengerId: passenger.id,
          title: passenger.title,
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          gender: passenger.gender,
          birthDate: passenger.dateOfBirth,
          phoneNumber: passenger.phoneNumber ? sanitizePhoneNumber(passenger.phoneNumber) : undefined,
          email: index === 0 ? userProfile?.email || undefined : undefined,
          infantPassengerId: '', // TODO: Deduce this based on age of infant in booking
          passportDetails: {
            passportNumber: passenger.passportNumber,
            issuingCountryIataCode: passenger.issuingCountry,
            expiryDate: passenger.expirationDate
          },
          baggageDetails: passengerBaggageDetails.length > 0 ? passengerBaggageDetails : null,
          seatingDetails: passengerSeatingDetails.length > 0 ? passengerSeatingDetails : null
        }
      })

      const addressPayload = {
        streetAddress: deliveryDetails.streetAddress,
        city: deliveryDetails.city,
        administrativeDivision: deliveryDetails.administrativeDivision,
        country: deliveryDetails.country,
        postalCode: deliveryDetails.postalCode
      }

      const payload: CreateFlightBookingRequest = {
        'external-system-offer-id': fareOffer.id,
        paymentIntentId: createFlightBookingPaymentIntentSuccess?.data?.paymentIntent?.id ?? '',
        passengers,
        deliveryDetails: {
          ...(useSplitDeliverySteps ? { stayAddress: addressPayload } : { deliveryAddress: addressPayload }),
          uspsLocationId: deliveryDetails.selectedUSPSLocation?.locationID ?? ''
        }
      }

      localStorage.setItem(FLIGHT_BOOKING_SUBMISSION_STORAGE_KEY, payload)

      if (!isTransfer) {
        // TODO: Check if Duffel offer is still valid before submitting payment
        // New endpoint will need to be created to check if the offer is still valid
        paymentFormRef.current?.submitPayment()
      } else {
        handleContinueToTransfer()
      }
    }
  }

  const handlePaymentSubmission = () => {
    if (useSplitDeliverySteps && deliveryDetails?.selectedUSPSLocation) {
      const { locationData, locationID } = deliveryDetails.selectedUSPSLocation

      dispatch(
        validateUspsAddress({
          request: {
            streetAddress: locationData.address,
            city: locationData.city,
            state: locationData.state,
            ZIPCode: buildUspsZipCodeForAddressRequest(locationData)
          },
          onSuccess: () => {
            dispatch(resetValidateUspsAddress())
            proceedWithPaymentSubmission()
          },
          onError: () => {
            dispatch(resetValidateUspsAddress())

            setDisabledLocationIDs((prev) => new Set(prev).add(locationID))

            setDeliveryDetails({
              ...deliveryDetails,
              selectedUSPSLocation: null
            })

            setActiveStep(dropoffStepIndex)

            Snackbar.show({
              message: 'This USPS location address could not be validated. Please select another location.',
              severity: 'error'
            })
          }
        })
      )

      return
    }

    proceedWithPaymentSubmission()
  }

  const renderStepHeader = () => {
    let headerTitle = ''
    let headerSubtitle = ''

    if (useSplitDeliverySteps) {
      switch (activeStep) {
        case 0:
          break

        case 1:
          headerTitle = 'Passenger Details'
          headerSubtitle = 'Provide your passenger information using the fields below.'
          break

        case stayAddressStepIndex:
          headerTitle = 'Stay Address'
          headerSubtitle = 'Provide your stay address using the fields below.'
          break

        case dropoffStepIndex:
          headerTitle = 'Dropoff Location'
          headerSubtitle = 'Choose a USPS drop-off location for your packages.'
          break

        case paymentStepIndex:
          headerTitle = 'Payment Details'
          headerSubtitle = 'Set up a card that can be used to pay for your trips.'
          break

        case receivePaymentStepIndex:
          if (isTransfer) {
            headerTitle = 'Payout Details'
            headerSubtitle = 'Connect a bank account (preferably in USD) to receive payments.'
          }
          break

        default:
          return null
      }
    } else {
      switch (activeStep) {
        case 0:
          break

        case 1:
          headerTitle = 'Passenger Details'
          headerSubtitle = 'Provide your passenger information using the fields below.'
          break

        case 2:
          headerTitle = 'Delivery Details'
          headerSubtitle = 'Provide your delivery information using the fields below.'
          break

        case 3:
          headerTitle = 'Payment Details'
          headerSubtitle = 'Set up a card that can be used to pay for your trips.'
          break

        case 4:
          if (isTransfer) {
            headerTitle = 'Payout Details'
            headerSubtitle = 'Connect a bank account (preferably in USD) to receive payments.'
          }
          break

        default:
          return null
      }
    }

    return (
      <Box sx={styles.stepHeader}>
        <Typography variant="h4" sx={styles.title}>
          {headerTitle}
        </Typography>

        {headerSubtitle && (
          <Typography variant="subtitle1" sx={styles.subtitle}>
            {headerSubtitle}
          </Typography>
        )}
      </Box>
    )
  }

  const uspsRadiusMiles = useMemo(() => {
    const value = env.USPS_LOCATION_SEARCH_RADIUS_MILES
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : 10
  }, [])

  const renderStepContent = () => {
    const checkoutMode = isTransfer ? 'setup' : 'payment'
    let handleCreateFlightBookingPaymentIntentRequest = null

    if (activeStep === 1) {
      if (!fareOffer) {
        return null
      }

      return (
        <PassengerDetails
          fareOffer={fareOffer}
          onFormChange={handleFormChange}
          countriesAndCities={countriesAndCities?.data as CountryAndCities[]}
          userProfile={userProfile}
          onBaggageSelectionsChange={onBaggageSelectionsChange}
          onSeatSelectionsChange={onSeatSelectionsChange}
        />
      )
    }

    if (useSplitDeliverySteps && activeStep === stayAddressStepIndex) {
      return (
        <DeliveryDetails
          mode="stayAddressOnly"
          sectionTitle="Stay Address"
          initialValues={deliveryDetails}
          onFormChange={handleDeliveryDetailsChange}
          arrivalAirportIataCode={arrivalAirportIataCode}
          flightDates={
            fareOffer?.slices?.length >= 2
              ? {
                  departureDate: date(fareOffer.slices[0].segments[0].departing_at).dateTime,
                  returnDate: date(
                    fareOffer.slices[fareOffer.slices.length - 1].segments[
                      fareOffer.slices[fareOffer.slices.length - 1].segments.length - 1
                    ].arriving_at
                  ).dateTime
                }
              : undefined
          }
          sourceCountry={effectiveSourceCountry}
          countriesAndCities={countriesAndCities?.data as CountryAndCities[]}
          deliveryToTravelerWindow={flightItinerary?.itinerary?.deliveryToTravelerWindow}
        />
      )
    }

    if (useSplitDeliverySteps && activeStep === dropoffStepIndex) {
      if (!deliveryDetails) return null

      return (
        <DropoffLocationStep
          deliveryDetails={deliveryDetails}
          onFormChange={handleDeliveryDetailsChange}
          arrivalAirportIataCode={arrivalAirportIataCode}
          radiusMiles={uspsRadiusMiles}
          disabledLocationIDs={disabledLocationIDs}
        />
      )
    }

    if (!useSplitDeliverySteps && activeStep === 2) {
      return (
        <DeliveryDetails
          onFormChange={handleDeliveryDetailsChange}
          arrivalAirportIataCode={arrivalAirportIataCode}
          isFulfillmentOrder={false}
          flightDates={
            fareOffer?.slices?.length >= 2
              ? {
                  departureDate: date(fareOffer.slices[0].segments[0].departing_at).dateTime,
                  returnDate: date(
                    fareOffer.slices[fareOffer.slices.length - 1].segments[
                      fareOffer.slices[fareOffer.slices.length - 1].segments.length - 1
                    ].arriving_at
                  ).dateTime
                }
              : undefined
          }
          sourceCountry={effectiveSourceCountry}
          countriesAndCities={countriesAndCities?.data as CountryAndCities[]}
          deliveryToTravelerWindow={flightItinerary?.itinerary?.deliveryToTravelerWindow}
        />
      )
    }

    if (activeStep === paymentStepIndex) {
      handleCreateFlightBookingPaymentIntentRequest = () => {
        const payload: CreateFlightBookingPaymentIntentRequestPayload = {
          request: {
            externalFlightBookingOfferId: fareOffer.id,
            flightItineraryId: flightItinerary.id,
            additionalServiceIds: [
              ...Object.values(baggageSelections).flatMap((passengerSelections) =>
                Object.values(passengerSelections).flatMap((baggage) => baggage.map((b) => b.baggageId))
              ),
              ...Object.values(seatSelections).flatMap((passengerSeats) =>
                Object.values(passengerSeats).map((seat) => seat.serviceId)
              )
            ]
          }
        }

        dispatch(createFlightBookingPaymentIntent(payload))
      }

      return (
        <PaymentDetails
          mode={checkoutMode}
          redirectPath={routes.flightBooking.path}
          setupIntent={flightBookingPaymentIntent?.data?.setupIntent}
          paymentIntent={flightBookingPaymentIntent?.data?.paymentIntent}
          customerSession={flightBookingPaymentIntent?.data?.customerSession}
          currency={flightBookingPaymentIntent?.data?.currency}
          paymentIntentLoading={createFlightBookingPaymentIntentLoading}
          handleCreatePaymentIntentRequest={handleCreateFlightBookingPaymentIntentRequest}
          setIsPaymentFormValid={setIsPaymentFormValid}
          setPaymentRequestLoading={setPaymentRequestLoading}
          setSetupPaymentLoading={setSetupPaymentLoading}
          paymentFormRef={paymentFormRef}
        />
      )
    } else if (activeStep === receivePaymentStepIndex && isTransfer) {
      const isAlreadyOnboardedToStripeConnect = Boolean(
        flightBookingPaymentIntent?.data?.isCustomerOnboardedToStripeConnect
      )

      handleCreateFlightBookingPaymentIntentRequest = () => {
        if (!flightItinerary?.id) {
          navigate(routes.requests.path)

          return
        }
        const payload: CreateFlightBookingPaymentIntentRequestPayload = {
          request: {
            externalFlightBookingOfferId: fareOffer.id,
            flightItineraryId: flightItinerary.id,
            additionalServiceIds: [
              ...Object.values(baggageSelections).flatMap((passengerSelections) =>
                Object.values(passengerSelections).flatMap((baggage) => baggage.map((b) => b.baggageId))
              ),
              ...Object.values(seatSelections).flatMap((passengerSeats) =>
                Object.values(passengerSeats).map((seat) => seat.serviceId)
              )
            ]
          }
        }

        dispatch(createFlightBookingPaymentIntent(payload))
      }

      if (isAlreadyOnboardedToStripeConnect) {
        return (
          <Alert severity="success" sx={styles.disclaimerAlert}>
            Your payout account is connected. Submit your order to complete the booking.
          </Alert>
        )
      }

      return (
        <PaymentDetails
          key={forceReloadPaymentForm ? 'reload' : 'normal'}
          mode={'setup'}
          redirectPath={routes.flightBooking.path}
          setupIntent={flightBookingPaymentIntent?.data?.setupIntent}
          paymentIntent={flightBookingPaymentIntent?.data?.paymentIntent}
          accountSession={accountSession ?? undefined}
          customerSession={flightBookingPaymentIntent?.data?.customerSession}
          currency={flightBookingPaymentIntent?.data?.currency}
          paymentIntentLoading={createFlightBookingPaymentIntentLoading}
          handleCreatePaymentIntentRequest={handleCreateFlightBookingPaymentIntentRequest}
          setIsPaymentFormValid={setIsPaymentFormValid}
          setPaymentRequestLoading={setPaymentRequestLoading}
          isTransfer
          onOnboardingComplete={() => setIsOnboadingComplete(true)}
        />
      )
    } else if (activeStep === confirmationStepIndex) {
      let confirmationStepErrorState:
        | {
            apiResponseError: ApiErrorResponse
            button: { text: string; onClick: () => void }
          }
        | undefined

      if (createFlightBookingError) {
        const bookingApiError = createFlightBookingError
        const travelerFirstName = flightItinerary?.traveler?.firstName ?? ''
        const travelerLastName = flightItinerary?.traveler?.lastName ?? ''
        const travelerDisplayName = `${travelerFirstName} ${travelerLastName}`.trim()

        confirmationStepErrorState = {
          apiResponseError: bookingApiError,
          button: isFullRefundBookingError(bookingApiError.userFriendlyMessage)
            ? {
                text: 'Contact Support',
                onClick: () => {
                  openBookingRefundSupportEmail({
                    apiError: bookingApiError,
                    travelerDisplayName: travelerDisplayName || 'Traveler',
                    travelerUserId: flightItinerary?.traveler?.userId ?? ''
                  })
                }
              }
            : {
                text: 'Try Again',
                onClick: handleSubmitOrder
              }
        }
      }

      return (
        <PaymentSubmittedSuccessfully
          title="Flight Booked Successfully"
          description="Your flight was successfully booked. Confirmation emails will be sent to you shortly with your booking details."
          button={{
            text: 'View My Trip',
            onClick: () => navigate(routes.requests.path),
            buttonType: 'primary'
          }}
          loadingState={{
            loading: createFlightBookingLoading || getUpdatedFlightOfferLoading || checkOnboardingInfoLoading,
            text: 'Booking your flight'
          }}
          success={createFlightBookingSuccess !== null}
          errorState={confirmationStepErrorState}
        />
      )
    } else {
      return null
    }
  }

  const handleCreateAccountSession = useCallback(() => {
    setSetupPaymentLoading(true)

    if (showCountryDropdownModal) {
      setShowCountryDropdownModal(false)
    }

    const payload: CreateAccountSessionRequestPayload = {
      request: {
        countryCode: stripeConnectAccountCountry
      },

      onSuccess: (response) => {
        if (flightBookingPaymentIntent) {
          const accountSession = {
            secret: response.data.secret,
            expiresAt: response.data.expiresAt
          }

          localStorage.setItem(CREATE_FLIGHT_BOOKING_PAYMENT_INTENT_STORAGE_KEY, flightBookingPaymentIntent)
          localStorage.setItem(ACCOUNT_SESSION_STORAGE_KEY, accountSession)
          localStorage.setItem(FARE_OFFER_STORAGE_KEY, fareOffer)

          paymentFormRef.current?.submitPayment()
        }

        setSetupPaymentLoading(false)
      },

      onError: () => {
        setSetupPaymentLoading(false)
      }
    }

    dispatch(createAccountSession(payload))
  }, [dispatch, flightBookingPaymentIntent, fareOffer, stripeConnectAccountCountry, showCountryDropdownModal])

  const handleCountryChange = useCallback((value: string) => {
    setStripeConnectAccountCountry(value)
  }, [])

  const handleContinueToTransfer = useCallback(() => {
    const isCustomerOnboardedToStripeConnect =
      isTransfer && flightBookingPaymentIntent?.data?.isCustomerOnboardedToStripeConnect

    if (!isCustomerOnboardedToStripeConnect) {
      setShowCountryDropdownModal(true)

      return
    }

    // Already onboarded: persist payout state for the Stripe setup redirect, then confirm
    // the card setup. Creating a Connect account session here would be claimed on remount
    // and Stripe returns cannot_create_account_session / already claimed.
    if (flightBookingPaymentIntent) {
      localStorage.setItem(CREATE_FLIGHT_BOOKING_PAYMENT_INTENT_STORAGE_KEY, flightBookingPaymentIntent)
      localStorage.setItem(FARE_OFFER_STORAGE_KEY, fareOffer)
    }

    paymentFormRef.current?.submitPayment()
  }, [isTransfer, flightBookingPaymentIntent, fareOffer])

  const getDisclaimerAlert = () => {
    if (paymentRequestLoading) {
      return (
        <Alert severity="warning" sx={styles.disclaimerAlert}>
          Processing your booking. Please do not navigate away until the booking is complete.
        </Alert>
      )
    } else {
      return null
    }
  }

  const renderButtons = () => {
    if (activeStep === 1) {
      return (
        <Button
          text="Continue to Delivery Details"
          fullWidth
          buttonType="primary"
          onClick={handleContinueToDeliveryDetails}
          disabled={!isPassengerDetailsFormValid || !passengerDetails}
        />
      )
    }

    if (useSplitDeliverySteps && activeStep === stayAddressStepIndex) {
      return (
        <Button
          text="Continue"
          fullWidth
          variant="contained"
          buttonType="primary"
          onClick={() => setActiveStep(dropoffStepIndex)}
          disabled={!isDeliveryDetailsFormValid || !deliveryDetails}
        />
      )
    }

    if (useSplitDeliverySteps && activeStep === dropoffStepIndex) {
      return (
        <Button
          text="Continue to Payment"
          fullWidth
          variant="contained"
          buttonType="primary"
          onClick={handleContinueToPayment}
          disabled={!deliveryDetails?.selectedUSPSLocation || !deliveryDetails}
          loading={validateAddressLoading}
        />
      )
    }

    if (!useSplitDeliverySteps && activeStep === 2) {
      return (
        <Button
          text="Continue to Payment"
          fullWidth
          variant="contained"
          buttonType="primary"
          onClick={handleContinueToPayment}
          disabled={!isDeliveryDetailsFormValid || !deliveryDetails}
        />
      )
    }

    if (activeStep === paymentStepIndex) {
      return (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          buttonType="primary"
          text={isTransfer ? 'Continue to Payout' : 'Submit Order'}
          loading={
            paymentRequestLoading || setupPaymentLoading || createAccountSessionLoading || validateAddressLoading
          }
          onClick={handlePaymentSubmission}
          disabled={
            !isTransfer
              ? !isPaymentFormValid || isTransfer || paymentRequestLoading
              : createAccountSessionLoading || validateAddressLoading
          }
          tooltipText={!isPaymentFormValid ? 'Please ensure all required fields are filled correctly.' : ''}
        />
      )
    }

    if (activeStep === receivePaymentStepIndex && isTransfer) {
      return (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          buttonType="primary"
          text="Submit Order"
          loading={createFlightBookingLoading || checkOnboardingInfoLoading}
          onClick={handleSubmitOrder}
          disabled={!isOnboadingComlpete}
          tooltipText={!isOnboadingComlpete ? 'Please complete the onboarding process.' : ''}
        />
      )
    }

    return null
  }

  const getOverviewButton = (): AccordionButtonProps | undefined => {
    if (activeStep === 1) {
      return {
        text: isDesktop ? 'Continue to Delivery Details' : 'Continue',
        onClick: handleContinueToDeliveryDetails,
        buttonType: 'primary',
        disabled: !isPassengerDetailsFormValid || !passengerDetails
      }
    }

    if (useSplitDeliverySteps && activeStep === stayAddressStepIndex) {
      return {
        text: 'Continue',
        onClick: () => setActiveStep(dropoffStepIndex),
        buttonType: 'primary',
        disabled: !isDeliveryDetailsFormValid || !deliveryDetails
      }
    }

    if (useSplitDeliverySteps && activeStep === dropoffStepIndex) {
      return {
        text: isDesktop ? 'Continue to Payment' : 'Continue',
        onClick: handleContinueToPayment,
        buttonType: 'primary',
        disabled: !deliveryDetails?.selectedUSPSLocation || !deliveryDetails,
        loading: validateAddressLoading
      }
    }

    if (!useSplitDeliverySteps && activeStep === 2) {
      return {
        text: isDesktop ? 'Continue to Payment' : 'Continue',
        onClick: handleContinueToPayment,
        buttonType: 'primary',
        disabled: !isDeliveryDetailsFormValid || !deliveryDetails
      }
    }

    if (activeStep === paymentStepIndex) {
      return {
        text: isDesktop ? (isTransfer ? 'Continue to Payout' : 'Submit Order') : isTransfer ? 'Continue' : 'Submit',
        onClick: handlePaymentSubmission,
        buttonType: 'primary',
        loading: paymentRequestLoading || setupPaymentLoading || createAccountSessionLoading || validateAddressLoading,
        disabled: !isTransfer
          ? !isPaymentFormValid || isTransfer || paymentRequestLoading
          : createAccountSessionLoading || validateAddressLoading
      }
    }

    if (activeStep === receivePaymentStepIndex && isTransfer) {
      return {
        text: 'Submit Order',
        onClick: handleSubmitOrder,
        buttonType: 'primary',
        loading: createFlightBookingLoading || checkOnboardingInfoLoading,
        disabled: !isOnboadingComlpete
      }
    }

    return undefined
  }

  let isOverviewSectionVisible =
    (activeStep !== (isTransfer ? receivePaymentStepIndex : paymentStepIndex) ||
      setupPaymentDetailsSuccess ||
      paymentSuccess) &&
    activeStep !== confirmationStepIndex

  if (!isTransfer && activeStep === paymentStepIndex) {
    isOverviewSectionVisible = true
  }

  const shouldShowStepperArrows = activeStep > 0 && activeStep < steps.length - 1

  const isStepperRightArrowDisabled = useMemo(() => {
    if (activeStep === 1) {
      return !isPassengerDetailsFormValid || !passengerDetails
    }

    if (useSplitDeliverySteps && activeStep === stayAddressStepIndex) {
      return !isDeliveryDetailsFormValid || !deliveryDetails
    }

    if (useSplitDeliverySteps && activeStep === dropoffStepIndex) {
      return !deliveryDetails?.selectedUSPSLocation || !deliveryDetails || validateAddressLoading
    }

    if (!useSplitDeliverySteps && activeStep === 2) {
      return !isDeliveryDetailsFormValid || !deliveryDetails
    }

    if (activeStep === paymentStepIndex) {
      if (!isTransfer) {
        return (
          !isPaymentFormValid ||
          paymentRequestLoading ||
          setupPaymentLoading ||
          createAccountSessionLoading ||
          validateAddressLoading
        )
      }

      return paymentRequestLoading || setupPaymentLoading || createAccountSessionLoading || validateAddressLoading
    }

    if (activeStep === receivePaymentStepIndex && isTransfer) {
      return !isOnboadingComlpete || createFlightBookingLoading || checkOnboardingInfoLoading
    }

    return false
  }, [
    activeStep,
    checkOnboardingInfoLoading,
    createAccountSessionLoading,
    createFlightBookingLoading,
    deliveryDetails,
    isDeliveryDetailsFormValid,
    isOnboadingComlpete,
    isPassengerDetailsFormValid,
    isPaymentFormValid,
    isTransfer,
    passengerDetails,
    paymentRequestLoading,
    paymentStepIndex,
    receivePaymentStepIndex,
    setupPaymentLoading,
    stayAddressStepIndex,
    dropoffStepIndex,
    useSplitDeliverySteps,
    validateAddressLoading
  ])

  return (
    <Stepper
      stepperWidth="70%"
      activeStep={activeStep}
      steps={steps}
      showStepper
      showLeftArrow={shouldShowStepperArrows}
      showRightArrow={shouldShowStepperArrows}
      isRightArrowDisabled={shouldShowStepperArrows && isStepperRightArrowDisabled}
      onStepClick={handleStepClick}
    >
      {renderStepHeader()}

      <Grid container spacing={isOverviewSectionVisible ? 4 : 0} sx={styles.gridContainer}>
        <Grid item xs={12} md={isOverviewSectionVisible ? 9 : 12}>
          <Box sx={styles.contentContainer} id="step-content-container">
            {renderStepContent()}
          </Box>
        </Grid>

        {isOverviewSectionVisible && (
          <Grid item xs={12} sm={3}>
            <Box sx={styles.tripOverviewContainer}>
              <TripOverview
                offerData={fareOffer}
                baggageSelections={baggageSelections}
                seatSelections={seatSelections}
                currency={flightBookingPaymentIntent?.data?.currency}
                discount={flightBookingPaymentIntent?.data?.discount}
                tax={flightBookingPaymentIntent?.data?.tax}
                fees={flightBookingPaymentIntent?.data?.fees}
                subtotal={flightBookingPaymentIntent?.data?.subtotal}
                total={flightBookingPaymentIntent?.data?.total}
                overviewAccordionButton={getOverviewButton()}
              />
            </Box>

            <ResponsiveDisplayWrapper isDesktop={true}>{renderButtons()}</ResponsiveDisplayWrapper>

            {getDisclaimerAlert()}
          </Grid>
        )}

        <Modal
          open={showCountryDropdownModal}
          onClose={() => setShowCountryDropdownModal(false)}
          title="Select Country"
          subtitle="Please select the country in which the bank account you'd like to receive payments in is registered."
          body={
            <Dropdown
              options={countries.map((country) => ({
                value: country.code,
                label: country.name
              }))}
              name="country"
              label="Country"
              value={stripeConnectAccountCountry}
              setValue={handleCountryChange}
              placeholder="Select Country"
              marginTop={3}
            />
          }
          primaryButton={{
            label: 'Continue',
            onClick: handleCreateAccountSession,
            disabled: !stripeConnectAccountCountry
          }}
          secondaryButton={{
            label: 'Dismiss',
            onClick: () => setShowCountryDropdownModal(false)
          }}
        />
      </Grid>
    </Stepper>
  )
}

export default FlightBooking
